/**
 * CENTRAL WEDDING CONFIGURATION
 *
 * This single file holds every editable piece of wedding information.
 * To create a new wedding, edit the values below — no component code
 * should ever need to change for normal customization.
 *
 * Related files:
 *   - src/data/weddingImages.ts  (all photo paths)
 *   - src/data/weddingTheme.ts   (colors + typography)
 */

export type RsvpType = 'whatsapp' | 'external';

export interface ImportantInfoItem {
  title: string;
  description: string;
  /** Set to false to hide this single item while keeping the section. */
  visible: boolean;
}

export interface WeddingSeoConfig {
  pageTitle: string;
  metaDescription: string;
  ogTitle: string;
  ogDescription: string;
  ogImage: string;
}

export interface WeddingConfig {
  // ── Names ────────────────────────────────────────────────
  brideName: string;
  groomName: string;

  // ── Date (shown separately for the large editorial layout) ─
  day: string;
  month: string;
  year: string;
  /** Full date string, e.g. "12 de Diciembre de 2026". */
  weddingDate: string;
  /** Numeric/string year, e.g. "2026". */
  weddingYear: string;

  // ── Hero ─────────────────────────────────────────────────
  heroSubtitle: string;
  scrollHint: string;

  // ── Introduction ─────────────────────────────────────────
  introductionText: string;
  introductionSubtext: string;

  // ── Ceremony ─────────────────────────────────────────────
  ceremonyName: string;
  ceremonyAddress: string;
  ceremonyTime: string;
  ceremonyMapsUrl: string;

  // ── Reception ────────────────────────────────────────────
  receptionName: string;
  receptionAddress: string;
  receptionTime: string;
  receptionMapsUrl: string;

  // ── Our story ────────────────────────────────────────────
  storyText: string;

  // ── Dress code ───────────────────────────────────────────
  dressCode: string;
  dressCodeNotes: string;
  /** Optional restriction line (e.g. "El color blanco está reservado..."). Empty string hides it. */
  dressCodeRestriction: string;

  // ── Important information ────────────────────────────────
  importantInformation: ImportantInfoItem[];

  // ── Gift registry ────────────────────────────────────────
  giftRegistryText: string;
  giftRegistryUrl: string;
  giftRegistryInfo: string;
  showBankTransfer: boolean;
  bankTransferInfo: string;

  // ── Accommodation ────────────────────────────────────────
  hotelName: string;
  hotelAddress: string;
  hotelReservationUrl: string;
  hotelReservationInfo: string;

  // ── RSVP ─────────────────────────────────────────────────
  rsvpType: RsvpType;
  rsvpUrl: string;
  /** Phone number with country code (no +), used only when rsvpType is "whatsapp". */
  rsvpWhatsappNumber: string;
  /** Prefilled message. Use [BRIDE NAME] and [GROOM NAME] as placeholders. */
  rsvpMessage: string;
  rsvpDeadline: string;
  contactInfo: string;

  // ── Countdown ────────────────────────────────────────────
  /** ISO date/time string the countdown targets. */
  countdownTargetDate: string;
  /** Shown when the wedding date has arrived or passed. */
  countdownCompleteMessage: string;

  // ── Footer ───────────────────────────────────────────────
  weddingHashtag: string;
  footerMessage: string;

  // ── Music (optional, disabled by default) ────────────────
  music: {
    enabled: boolean;
    src: string;
    title: string;
  };

  // ── Section toggles ──────────────────────────────────────
  showOurStory: boolean;
  showGallery: boolean;
  showDressCode: boolean;
  showImportantInformation: boolean;
  showGiftRegistry: boolean;
  showAccommodation: boolean;
  showWeddingHashtag: boolean;

  // ── SEO / social sharing ─────────────────────────────────
  seo: WeddingSeoConfig;
}

export const weddingConfig: WeddingConfig = {
  brideName: '[BRIDE NAME]',
  groomName: '[GROOM NAME]',

  day: '[DAY]',
  month: '[MONTH]',
  year: '[YEAR]',
  weddingDate: '[DAY] · [MONTH] · [YEAR]',
  weddingYear: '[YEAR]',

  heroSubtitle: 'Nos casamos',
  scrollHint: 'Desliza para descubrir',

  introductionText:
    'Hay momentos en la vida que son especiales por sí solos, pero compartirlos con las personas que queremos los convierte en inolvidables.',
  introductionSubtext: 'Tenemos el honor de invitarte a celebrar nuestra boda.',

  ceremonyName: '[VENUE NAME]',
  ceremonyAddress: '[ADDRESS]',
  ceremonyTime: '[TIME]',
  ceremonyMapsUrl: 'https://maps.google.com',

  receptionName: '[RECEPTION VENUE]',
  receptionAddress: '[ADDRESS]',
  receptionTime: '[TIME]',
  receptionMapsUrl: 'https://maps.google.com',

  storyText: '[Aquí podremos agregar posteriormente la historia de la pareja.]',

  dressCode: '[DRESS CODE]',
  dressCodeNotes: 'Nos encantará verte elegante para celebrar esta noche con nosotros.',
  dressCodeRestriction: 'El color blanco está reservado para la novia.',

  importantInformation: [
    { title: 'Solo adultos', description: '[Información pendiente]', visible: true },
    { title: 'Estacionamiento', description: '[Información pendiente]', visible: true },
    { title: 'Transporte', description: '[Información pendiente]', visible: true },
    { title: 'Puntualidad', description: '[Información pendiente]', visible: true },
    { title: 'Clima', description: '[Información pendiente]', visible: true },
    { title: 'Recomendaciones', description: '[Información pendiente]', visible: true },
  ],

  giftRegistryText: 'Su presencia es nuestro mejor regalo.',
  giftRegistryUrl: 'https://example.com/registry',
  giftRegistryInfo: '[GIFT REGISTRY]',
  showBankTransfer: false,
  bankTransferInfo: '[BANK TRANSFER INFORMATION]',

  hotelName: '[HOTEL NAME]',
  hotelAddress: '[ADDRESS]',
  hotelReservationUrl: 'https://example.com/hotel',
  hotelReservationInfo: '[RESERVATION INFORMATION]',

  rsvpType: 'external',
  rsvpUrl: 'https://forms.google.com',
  rsvpWhatsappNumber: '',
  rsvpMessage: 'Hola, quiero confirmar mi asistencia a la boda de [BRIDE NAME] y [GROOM NAME].',
  rsvpDeadline: '[RSVP DEADLINE]',
  contactInfo: '[CONTACT INFORMATION]',

  countdownTargetDate: '2026-12-31T16:00:00',
  countdownCompleteMessage: 'Hoy celebramos nuestro amor.',

  weddingHashtag: '[WEDDINGHASHTAG]',
  footerMessage: 'Con amor,',

  music: {
    enabled: false,
    src: '',
    title: '',
  },

  showOurStory: true,
  showGallery: true,
  showDressCode: true,
  showImportantInformation: true,
  showGiftRegistry: true,
  showAccommodation: true,
  showWeddingHashtag: true,

  seo: {
    pageTitle: '[BRIDE NAME] & [GROOM NAME] | Nuestra Boda',
    metaDescription:
      'Acompáñanos a celebrar nuestra boda. Será un honor compartir este día tan especial contigo.',
    ogTitle: '[BRIDE NAME] & [GROOM NAME] | Nuestra Boda',
    ogDescription:
      'Acompáñanos a celebrar nuestra boda. Será un honor compartir este día tan especial contigo.',
    ogImage: '/images/og-wedding.jpg',
  },
};